import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/MainContainer.css';
import api from '../wrapper/API';
import { useSuccessBar } from '../context/SuccessBarContext';
import { usePopup } from '../context/PopupContext';
import Popup from '../popup/Popup';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import CustomDropdown from '../input/CustomDropdown';
import ActionButton from '../input/ActionButton';
import SegmentedControl from '../input/SegmentedControl';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import { notifyRegistrationsChanged } from '../../hooks/useRegistrationsBadge';
import { normalizeEmail, normalizePhone, isEmail, isPhone } from '../../util/validation';
import { scoreClass } from '../../util/matchScore';
import { CandidateGroupMeta, MatchPillGroup } from './MatchPill';
import OfflineBanner from '../shell/OfflineBanner';
import { eventRules } from '../../util/eventRules';
import { ADD_ON_WAITLIST_SLOT, mailSlotLabel } from '../../util/mailSlots';
import { normalizeParticipantGroup } from '../../util/participantGroups';
import { addOnColorStyle, addOnCounterItems, addOnLineItems, hasAddOnColor } from '../../util/addOns';
import AddOnChips from '../add-on/AddOnChips';
import {
  eventAddOnsOf, requestedAddOnsOf, usedByAddOn, predictAdmission, simulateGroup, groupAddOnShortages,
  waitlistAddOnText, reductionNote, reducedAddOnsText, joinNames,
} from './registrationAdmission';
import './Registrations.css';

// Validatoren / Normalisierer kommen aus util/validation.js (1:1 zur Backend-Logik
// in ContactNormalizer.cs) — zentral, damit Anpassungen nicht hier UND in anderen
// Formularen nachgezogen werden müssen.
// MatchPill / MatchPillGroup (~140 Zeilen Sub-Komponente) ist ausgelagert in ./MatchPill.

// Vorkasse-Event: zahlungspflichtige Spieler bekommen beim Bestätigen die Zahlungsaufforderung —
// die verbindliche Bestätigung kommt erst mit "Als bezahlt markieren" (Spieltag-Ansicht).
const PAYMENT_REQUEST_MAIL = `Mail „${mailSlotLabel('payment_request')}“`;

// Anmeldung über den persönlichen Link (RegistrationSource MemberSelfSignup).
const isPersonalLinkSource = (r) => r.source === 'MemberSelfSignup' || r.source === 1;

// Waitlist reason of a prediction (registrationAdmission.predictAdmission): 'event' | 'addOn' | null.
const waitlistReasonOf = (prediction) => (prediction?.status === 'waitlist' ? 'event'
  : prediction?.status === 'addOnWaitlist' ? 'addOn'
  : null);

// Booked add-ons of a prediction in a comparable form ("addOnId:quantity", sorted; '' = none).
const bookedAddOnsKey = (prediction) => (prediction?.booked ?? [])
  .map(b => `${b.addOnId}:${b.quantity}`)
  .sort()
  .join(',');

// "„Leihausrüstung“ reicht" / "„Leihausrüstung“ und „Funk“ reichen" (group card hint).
const shortageSubject = (shortages) =>
  `${joinNames(shortages.map(s => `„${s.name}“`))} ${shortages.length === 1 ? 'reicht' : 'reichen'}`;

// "N Person möchte / Personen möchten …" for the add-on banners of the group dialog.
const personsWanting = (persons, quantity) => (quantity > persons
  ? `${persons} ${persons === 1 ? 'Person möchte' : 'Personen möchten'} insgesamt ${quantity}`
  : `${persons} ${persons === 1 ? 'Person möchte' : 'Personen möchten'} den Zusatz`);

// Seite für Anmeldungen aus dem WordPress-Formular (öffentlich oder privat per Einladungslink) und über
// den persönlichen Link.
// - Filterbar nach Spieltag, sortiert nach Eingangsdatum
// - Einzel-Anmeldungen als individuelle Karten
// - Sammelanmeldungen (groupToken gesetzt) als gemeinsame Karte
//   → Annehmen/Ablehnen gilt für alle Personen der Sammelanmeldung gleichzeitig
function Registrations() {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const { openPopup, closePopup, popupData } = usePopup();
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [busyGroup, setBusyGroup] = useState(null);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');

  // Zentraler Pending-State der durch alle Bestätigungs-Schritte fließt.
  const [pendingApproval, setPendingApproval] = useState(null);

  const [correctEmail, setCorrectEmail] = useState('');
  const [correctPhone, setCorrectPhone] = useState('');

  // State für den Kontaktdaten-Abweichungs-Dialog bei Einzelanmeldungen.
  // { reg, playerId, createNew, forceConfirm, diffs, candidate, keepOld }
  const [pendingContactDiff, setPendingContactDiff] = useState(null);
  const toggleContactDiffKeepOld = () =>
    setPendingContactDiff(prev => prev ? { ...prev, keepOld: !prev.keepOld } : prev);

  const toggleGroupMemberKeepOld = (regId) => {
    setGroupAcceptState(prev => ({
      ...prev,
      members: prev.members.map(m =>
        m.reg.id === regId ? { ...m, keepOld: !m.keepOld } : m
      )
    }));
  };

  // Modus der Sammelanmeldung: 'allConfirmed' / 'allWaitlist' setzen den Status aller Personen,
  // 'individual' lässt die aktuellen Status stehen und schaltet die Einzelwahl frei.
  const setGroupMode = (mode) => {
    setGroupAcceptState(prev => ({
      ...prev,
      mode,
      members: mode === 'individual'
        ? prev.members
        : prev.members.map(m => ({ ...m, status: mode === 'allConfirmed' ? 'confirmed' : 'waitlist' }))
    }));
  };

  const setGroupMemberStatus = (regId, status) => {
    setGroupAcceptState(prev => ({
      ...prev,
      members: prev.members.map(m =>
        m.reg.id === regId ? { ...m, status } : m
      )
    }));
  };

  // State für den Dialog "Sammelanmeldung annehmen".
  // members: [{ reg, decision: { playerId, createNew }, keepOld, status }] in Anmelde-Reihenfolge
  //   status: 'confirmed' | 'waitlist' | 'addOnWaitlist'
  // mode: 'allConfirmed' | 'allWaitlist' | 'individual' — nur relevant bei Konflikt, sonst null
  // hasCapacityConflict: nicht genug Plätze; availableSpots: verbleibende Plätze (Infinity ohne Limit)
  // addOnShortages: Zusätze mit Warteliste, von denen die Sammelanmeldung mehr möchte als frei ist (= Zusatz-Konflikt).
  //   Kürzungen von Zusätzen ohne Warteliste sind kein Konflikt (Hinweis je Mitglied, kein Force-Flag).
  // eventAddOns: Anmelde-Zusätze des Spieltags mit Belegung beim Öffnen des Dialogs.
  const [groupAcceptState, setGroupAcceptState] = useState(null);

  const load = () => {
    api.get('/Registration/Pending')
      .then(res => setItems(res.data ?? []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Eindeutige Spieltage extrahieren, aufsteigend sortiert.
  const eventOptions = useMemo(() => {
    const map = new Map();
    items.forEach(r => {
      if (!map.has(r.eventId)) {
        // RegistrationResponse carries the event's template rules (linkSignup/neverPublic) + eventLabel.
        const rules = eventRules(r);
        map.set(r.eventId, {
          eventId: r.eventId,
          eventDate: r.eventDate,
          confirmed: r.eventConfirmedCount ?? 0,
          maxPlayers: r.eventMaxPlayers ?? 0,
          // Anmelde-Zusätze des Prozesses mit Belegung (max 0 = unbegrenzt) — Zähler, Wartelisten-/Kürzungs-Hinweise.
          addOns: eventAddOnsOf(r),
          // Mehrere Spieltage am selben Datum sind nur über die Location unterscheidbar.
          locationName: r.locationName ?? null,
          // Anmeldung nur über den Einladungslink (früher "Privat") + Bezeichnung aus der Vorlage.
          linkSignup: rules.linkSignup,
          eventLabel: rules.eventLabel,
          count: 0
        });
      }
      map.get(r.eventId).count += 1;
    });
    return Array.from(map.values())
      .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
  }, [items]);

  useEffect(() => {
    if (eventOptions.length === 0) {
      if (selectedEvent !== '') setSelectedEvent('');
      return;
    }
    const stillExists = eventOptions.some(o => String(o.eventId) === String(selectedEvent));
    if (!stillExists) setSelectedEvent(String(eventOptions[0].eventId));
  }, [eventOptions, selectedEvent]);

  const visibleItems = useMemo(() => {
    if (!selectedEvent) return [];
    return items
      .filter(r => String(r.eventId) === String(selectedEvent))
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [items, selectedEvent]);

  // Registrierungen zu Einzel-Einträgen oder Sammelanmeldungen zusammenfassen.
  // Einzel = kein groupToken oder groupSize <= 1
  // Sammelanmeldung = mehrere Registrierungen mit demselben groupToken
  const groupedVisible = useMemo(() => {
    const result = [];
    const tokenMap = new Map(); // groupToken → index in result

    for (const r of visibleItems) {
      if (r.groupToken && r.groupSize > 1) {
        if (tokenMap.has(r.groupToken)) {
          result[tokenMap.get(r.groupToken)].members.push(r);
        } else {
          tokenMap.set(r.groupToken, result.length);
          result.push({ isGroup: true, groupToken: r.groupToken, members: [r] });
        }
      } else {
        result.push({ isGroup: false, reg: r });
      }
    }
    return result;
  }, [visibleItems]);

  const selectedEventInfo = eventOptions.find(o => String(o.eventId) === String(selectedEvent));

  // -------------------------------------------------------------------------
  //  Process (Approve / Reject) — Einzel-Registrierungen
  // -------------------------------------------------------------------------

  // paymentRequested: vom Backend gemeldet — nur dann ging wirklich die Zahlungsaufforderung raus
  // (nicht bei Spielern ohne Zahlungspflicht, ohne Mailadresse oder bei ausgeschalteter Mail der Gruppe).
  // reducedAddOns: add-ons without waitlist that were booked with less (or not at all) — appended to the text.
  const outcomeMessage = (outcome, createNew, paymentRequested = false, reducedAddOns = null) => {
    const base = (() => {
      switch (outcome) {
        case 'Confirmed':
          if (paymentRequested) return `${createNew ? 'Spieler angelegt & bestätigt' : 'Bestätigt'} – ${PAYMENT_REQUEST_MAIL}`;
          return createNew ? 'Spieler angelegt & bestätigt' : 'Anmeldung bestätigt';
        case 'Waitlist':       return createNew ? 'Spieler angelegt & auf Warteliste gesetzt' : 'Auf Warteliste gesetzt';
        case 'AddOnWaitlist':  return createNew ? 'Spieler angelegt & auf Zusatz-Warteliste gesetzt' : 'Auf Zusatz-Warteliste gesetzt';
        case 'AlreadyInEvent': return 'Spieler war bereits im Spieltag eingetragen';
        case 'Rejected':       return 'Anmeldung abgelehnt';
        default:               return 'Verarbeitet';
      }
    })();
    const reduced = reducedAddOnsText(reducedAddOns);
    return reduced ? `${base} – ${reduced}` : base;
  };

  // The counters of an event come with every processed registration: copy them into the remaining rows of
  // that event (confirmed count + sign-up add-on usage), so the header and the predictions stay current.
  const withFreshEventCounts = (rows, fresh) => {
    if (!fresh) return rows;
    return rows.map(r => r.eventId === fresh.eventId ? {
      ...r,
      eventConfirmedCount: fresh.eventConfirmedCount,
      eventMaxPlayers:     fresh.eventMaxPlayers,
      eventAddOns:         fresh.eventAddOns ?? r.eventAddOns,
    } : r);
  };

  const doProcess = (id, body) => {
    setBusyId(id);
    startSuccessBarLoading('Anmeldung wird verarbeitet');
    api.post(`/Registration/Process/${id}`, body)
      .then(res => {
        const outcome = res.data?.outcome ?? (body.approve ? 'Confirmed' : 'Rejected');
        startSuccessBarSuccess(outcomeMessage(outcome, body.createNewPlayer, !!res.data?.paymentRequested, res.data?.reducedAddOns));

        const fresh = res.data?.registration;
        setItems(prev => withFreshEventCounts(prev.filter(r => r.id !== id), fresh));
        notifyRegistrationsChanged();
      })
      .catch(err => {
        closeSuccessBar();
        setError(err?.response?.data ?? 'Verarbeitung fehlgeschlagen.');
        openPopup('registrationerror', id);
      })
      .finally(() => setBusyId(null));
  };

  const isEventFull = () => !!selectedEventInfo
    && selectedEventInfo.maxPlayers > 0
    && selectedEventInfo.confirmed >= selectedEventInfo.maxPlayers;

  // What approving this single registration leads to (R7 mirror, registrationAdmission): status, the exhausted
  // waitlist add-ons and the reductions of add-ons without waitlist. The candidates' "Bei Annahme" preview
  // (priceOnApproval / mailOnApproval) comes from the same simulation in the backend.
  const admissionOf = (r) => {
    const eventAddOns = selectedEventInfo?.addOns ?? [];
    return predictAdmission(requestedAddOnsOf(r), eventAddOns, usedByAddOn(eventAddOns), { eventFull: isEventFull() });
  };

  // Sammelanmeldung only: how a member's real approval differs from the single-approval preview of the backend
  // (priceOnApproval / mailOnApproval simulate this registration alone against the current usage) — order of the
  // members, capacity conflict, choice in the group dialog. status: what the member really gets ('confirmed' |
  // 'waitlist' | 'addOnWaitlist'); prediction: its cumulative prediction (the booked add-ons).
  // → null when the preview holds (MatchPill.approvalSummary shows price + mail), else { status: the differing
  // status | null, reduced: same status, but earlier members leave less of an add-on without waitlist } — the
  // previewed price is stale then and not shown.
  const groupApprovalOutcome = (r, status, prediction) => {
    const single = admissionOf(r);
    const statusDiffers = !!status && status !== single.status;
    const reduced = !statusDiffers && status === 'confirmed' && !!prediction
      && bookedAddOnsKey(prediction) !== bookedAddOnsKey(single);
    if (!statusDiffers && !reduced) return null;
    return { status: statusDiffers ? status : null, reduced };
  };

  // Popup state of a waitlist reason: 'event' | 'addOn' + the exhausted add-ons for the text.
  const waitlistPending = (r) => {
    const prediction = admissionOf(r);
    const reason = selectedEventInfo ? waitlistReasonOf(prediction) : null;
    return reason ? { waitlistReason: reason, waitlistAddOns: prediction.waitlistAddOns } : null;
  };

  const executeApproval = (pending, forceConfirm = false) => {
    doProcess(pending.regId, {
      playerId: pending.playerId,
      createNewPlayer: pending.createNew,
      approve: true,
      overrideEmail: pending.email || null,
      overridePhone: pending.phone || null,
      forceConfirm,
    });
  };

  const tryApprove = (r, playerId, createNew, forceConfirm = false) => {
    if (!createNew && playerId) {
      const cand = (r.matchCandidates ?? []).find(c => c.playerId === playerId);
      if (cand && cand.score >= 90) {
        const diffs = computeContactDiffs(r, cand);
        if (diffs.length > 0) {
          setPendingContactDiff({ reg: r, playerId, createNew, forceConfirm, diffs, candidate: cand, keepOld: true });
          openPopup('confirmcontactdiff', r.id);
          return;
        }
      }
    }
    continueApprove(r, playerId, createNew, true, forceConfirm);
  };

  const handleConfirmContactDiff = () => {
    if (!pendingContactDiff) return;
    const { reg, playerId, createNew, keepOld, forceConfirm } = pendingContactDiff;
    closePopup();
    continueApprove(reg, playerId, createNew, keepOld, forceConfirm);
  };

  const continueApprove = (r, playerId, createNew, keepOld, forceConfirm) => {
    let email = normalizeEmail(r.email);
    let phone = normalizePhone(r.phone);

    if (!createNew && playerId && keepOld) {
      const cand = (r.matchCandidates ?? []).find(c => c.playerId === playerId);
      if (cand) {
        email = normalizeEmail(cand.email);
        phone = normalizePhone(cand.phone);
      }
    }

    if (!isEmail(email) || !isPhone(phone)) {
      setPendingApproval({ regId: r.id, playerId, createNew, email, phone, forceConfirm });
      setCorrectEmail(email);
      setCorrectPhone(phone);
      openPopup('correctcontactdata', r.id);
      return;
    }

    const pending = { regId: r.id, playerId, createNew, email, phone, forceConfirm };

    if (!forceConfirm) {
      const waitlist = waitlistPending(r);
      if (waitlist) {
        setPendingApproval({ ...pending, ...waitlist });
        openPopup('confirmwaitlist', r.id);
        return;
      }
    }

    closePopup();
    executeApproval(pending, forceConfirm);
  };

  const handleConfirmCorrection = () => {
    const e = normalizeEmail(correctEmail);
    const p = normalizePhone(correctPhone);
    if (!isEmail(e)) {
      setError('Email-Format ungültig. Bitte korrigieren.');
      openPopup('registrationerror', pendingApproval?.regId);
      return;
    }
    if (!isPhone(p)) {
      setError('Telefonnummer muss im Format +<Ländervorwahl><Nummer> vorliegen, z. B. +4915123456789.');
      openPopup('registrationerror', pendingApproval?.regId);
      return;
    }
    const updated = { ...pendingApproval, email: e, phone: p };
    setPendingApproval(updated);
    closePopup();

    if (!updated.forceConfirm) {
      const regItem = items.find(i => i.id === updated.regId);
      const waitlist = regItem ? waitlistPending(regItem) : null;
      if (waitlist) {
        setPendingApproval({ ...updated, ...waitlist });
        openPopup('confirmwaitlist', updated.regId);
        return;
      }
    }
    executeApproval(updated, updated.forceConfirm ?? false);
  };

  const handleConfirmWaitlist = () => {
    if (!pendingApproval) return;
    closePopup();
    executeApproval(pendingApproval);
  };

  const handleForceConfirm = () => {
    if (!pendingApproval) return;
    closePopup();
    executeApproval(pendingApproval, true);
  };

  const askReject = (r) => openPopup('confirmrejectregistration', r.id);
  const handleConfirmReject = () => {
    const id = popupData?.identifier;
    if (!id) return;
    closePopup();
    doProcess(id, { playerId: null, createNewPlayer: false, approve: false });
  };

  const askPickMatch = (r) => openPopup('pickmatchedplayer', r.id);
  const handlePickMatch = (r, candidate) => {
    tryApprove(r, candidate.playerId, false);
  };

  // -------------------------------------------------------------------------
  //  Sammelanmeldungen (groupToken)
  // -------------------------------------------------------------------------

  const askRejectGroup = (groupToken) => openPopup('confirmrejectgroup', groupToken);

  const handleConfirmRejectGroup = () => {
    const groupToken = popupData?.identifier;
    if (!groupToken) return;
    closePopup();
    setBusyGroup(groupToken);
    startSuccessBarLoading('Sammelanmeldung wird abgelehnt');
    api.post(`/Registration/RejectGroup/${groupToken}`)
      .then(res => {
        const count = res.data?.rejectedCount ?? 0;
        setItems(prev => prev.filter(r => r.groupToken !== groupToken));
        startSuccessBarSuccess(`${count} ${count === 1 ? 'Anmeldung' : 'Anmeldungen'} abgelehnt`);
        notifyRegistrationsChanged();
      })
      .catch(() => {
        closeSuccessBar();
        setError('Ablehnung fehlgeschlagen.');
        openPopup('registrationerror', groupToken);
      })
      .finally(() => setBusyGroup(null));
  };

  const availableSpotsOfEvent = () => (selectedEventInfo?.maxPlayers > 0
    ? Math.max(0, selectedEventInfo.maxPlayers - selectedEventInfo.confirmed)
    : Infinity);

  // Prediction for a Sammelanmeldung without a dialog choice: members in registration order, cumulative —
  // exactly what the backend does when no force flag is sent (cards of the group, initial dialog state).
  const predictGroupDefault = (regs) => simulateGroup(
    regs.map(reg => ({ reg, status: 'confirmed' })),
    selectedEventInfo?.addOns ?? [],
    { explicit: false }
  ).results;

  const openGroupAccept = (group) => {
    const availableSpots = availableSpotsOfEvent();
    const hasCapacityConflict = availableSpots < group.members.length;
    const eventAddOns = selectedEventInfo?.addOns ?? [];
    // Zusatz-Konflikt: von einem Zusatz mit Warteliste möchte die Sammelanmeldung mehr, als frei ist.
    const addOnShortages = groupAddOnShortages(group.members, eventAddOns);
    const hasAddOnConflict = addOnShortages.length > 0;
    const predicted = predictGroupDefault(group.members);

    // Vorauswahl: Platz-Konflikt → ganze Sammelanmeldung auf Warteliste.
    // Nur Zusatz-Konflikt → Einzelwahl, wer (in Anmelde-Reihenfolge) nicht mehr passt, auf die Zusatz-Warteliste.
    const mode = hasCapacityConflict ? 'allWaitlist'
      : hasAddOnConflict ? 'individual'
      : null;

    const members = group.members.map(reg => {
      const candidates = goodMatches(reg);
      const topHigh = candidates.find(c => c.score >= 90);
      const decision = topHigh
        ? { playerId: topHigh.playerId, createNew: false }
        : { playerId: null, createNew: true };
      const hasDiffs = topHigh ? computeContactDiffs(reg, topHigh).length > 0 : false;

      let status = 'confirmed';
      if (mode === 'allWaitlist') {
        status = 'waitlist';
      } else if (mode === 'individual' && predicted.get(reg.id)?.status === 'addOnWaitlist') {
        status = 'addOnWaitlist';
      }

      return { reg, decision, keepOld: hasDiffs, status };
    });
    setGroupAcceptState({
      groupToken: group.groupToken, members, mode, hasCapacityConflict, availableSpots, addOnShortages, eventAddOns,
    });
    openPopup('acceptgroup', group.groupToken);
  };

  const setMemberDecision = (regId, decision) => {
    setGroupAcceptState(prev => ({
      ...prev,
      members: prev.members.map(m => {
        if (m.reg.id !== regId) return m;
        const candidate = !decision.createNew && decision.playerId
          ? (m.reg.matchCandidates ?? []).find(c => c.playerId === decision.playerId)
          : null;
        const hasDiffs = candidate && candidate.score >= 90
          ? computeContactDiffs(m.reg, candidate).length > 0
          : false;
        return { ...m, decision, keepOld: hasDiffs };
      })
    }));
  };

  // Zusammenfassung der aktuellen Auswahl im Dialog der Sammelanmeldung: Button-Label, Überbuchungs-Hinweise und
  // die kumulative Vorhersage je Mitglied (Kürzungen von Zusätzen ohne Warteliste, Zusatz-Warteliste).
  const groupAcceptSummary = useMemo(() => {
    if (!groupAcceptState) return null;
    const { members, mode, availableSpots, eventAddOns } = groupAcceptState;
    const confirmed = members.filter(m => m.status === 'confirmed');
    const waitlisted = members.length - confirmed.length;
    // With a conflict the dialog's choice is sent 1:1 (force flags), otherwise the backend decides in order.
    const { results, overbooked } = simulateGroup(members, eventAddOns ?? [], { explicit: !!mode });
    const confirmText = !mode
      ? 'Alle annehmen'
      : waitlisted === 0
        ? `${confirmed.length} bestätigen`
        : confirmed.length === 0
          ? `${waitlisted} auf Warteliste`
          : `${confirmed.length} bestätigen · ${waitlisted} Warteliste`;
    return {
      confirmText,
      // availableSpots = Infinity (kein Limit) → Ergebnis 0
      spotsOverbooked: Math.max(0, confirmed.length - availableSpots),
      // [{ addOnId, name, by }]: Zusätze mit Warteliste, die durch "Bestätigt" über ihr Limit gebucht werden.
      addOnsOverbooked: overbooked,
      // regId → { status, waitlistAddOns, reductions } (kumulativ in Anmelde-Reihenfolge)
      predictions: results,
    };
  }, [groupAcceptState]);

  const handleConfirmGroupAccept = () => {
    if (!groupAcceptState) return;
    closePopup();

    // Bei Konflikt (Plätze oder Zusatz mit Warteliste) gilt die Auswahl im Dialog 1:1 (Force-Flags) — das Backend
    // prüft dann nicht erneut gegen evtl. veraltete Zahlen. Ohne Konflikt entscheidet das Backend (in
    // Anmelde-Reihenfolge). Kürzungen von Zusätzen ohne Warteliste sind kein Konflikt und senden kein Flag.
    const { hasCapacityConflict, addOnShortages } = groupAcceptState;
    const explicit = hasCapacityConflict || (addOnShortages ?? []).length > 0;

    const decisions = groupAcceptState.members.map(m => {
      const candidate = !m.decision.createNew && m.decision.playerId
        ? (m.reg.matchCandidates ?? []).find(c => c.playerId === m.decision.playerId)
        : null;
      const diffs = candidate && candidate.score >= 90
        ? computeContactDiffs(m.reg, candidate)
        : [];
      const useOldData = diffs.length > 0 && m.keepOld;

      return {
        registrationId: m.reg.id,
        playerId: m.decision.createNew ? null : m.decision.playerId,
        createNewPlayer: m.decision.createNew,
        overrideEmail: useOldData ? (normalizeEmail(candidate.email) || null) : null,
        overridePhone: useOldData ? (normalizePhone(candidate.phone) || null) : null,
        forceConfirm: explicit && m.status === 'confirmed',
        forceWaitlist: m.status === 'waitlist',
        forceAddOnWaitlist: m.status === 'addOnWaitlist',
      };
    });

    const token = groupAcceptState.groupToken;
    setBusyGroup(token);
    startSuccessBarLoading('Sammelanmeldung wird verarbeitet');
    api.post('/Registration/ProcessGroup', { groupToken: token, decisions })
      .then(res => {
        const results = res.data?.results ?? [];
        const processedIds = new Set(results.map(r => r.registration.id));
        const lastFresh = results.length > 0 ? results[results.length - 1].registration : null;
        const confirmed = results.filter(r => r.outcome === 'Confirmed').length;
        const waitlisted = results.filter(r => r.outcome === 'Waitlist' || r.outcome === 'AddOnWaitlist').length;
        const addOnWaitlisted = results.filter(r => r.outcome === 'AddOnWaitlist').length;

        setItems(prev => withFreshEventCounts(prev.filter(r => !processedIds.has(r.id)), lastFresh));

        const waitlistText = addOnWaitlisted > 0
          ? `${waitlisted} auf Warteliste (davon ${addOnWaitlisted} Zusatz-Warteliste)`
          : `${waitlisted} auf Warteliste`;
        const msg = confirmed > 0 && waitlisted > 0
          ? `${confirmed} bestätigt, ${waitlistText}`
          : confirmed > 0
            ? `${confirmed} Spieler bestätigt`
            : addOnWaitlisted > 0 ? waitlistText : `${waitlisted} Spieler auf Warteliste`;
        const paymentRequests = results.filter(r => r.paymentRequested).length;
        // Reductions of add-ons without waitlist, over all members: "BBs auf 1 gekürzt, …".
        const reduced = reducedAddOnsText(results.flatMap(r => r.reducedAddOns ?? []));
        startSuccessBarSuccess([
          msg,
          paymentRequests > 0 ? `${paymentRequests}× ${PAYMENT_REQUEST_MAIL}` : null,
          reduced || null,
        ].filter(Boolean).join(' – '));
        notifyRegistrationsChanged();
      })
      .catch(err => {
        closeSuccessBar();
        setError(err?.response?.data ?? 'Verarbeitung fehlgeschlagen.');
        openPopup('registrationerror', token);
      })
      .finally(() => setBusyGroup(null));
  };

  // -------------------------------------------------------------------------
  //  Helpers
  // -------------------------------------------------------------------------
  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };
  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${d.getFullYear()} · ${hh}:${mi}`;
  };

  const cardVariants = {
    initial: { opacity: 0, y: 6 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { delay: 0.04 * i, duration: 0.3 }
    })
  };

  const matchPickItem = items.find(i => i.id === popupData?.identifier) || {};

  const goodMatches = (r) => (r.matchCandidates ?? []).filter(c => c.score >= 70);

  const computeContactDiffs = (reg, match) => {
    if (!match) return [];
    const diffs = [];
    const regEmail = normalizeEmail(reg.email);
    const regPhone = normalizePhone(reg.phone);
    const mEmail   = normalizeEmail(match.email);
    const mPhone   = normalizePhone(match.phone);
    const regFirst = (reg.firstName ?? '').trim();
    const regLast  = (reg.lastName ?? '').trim();
    const mFirst   = (match.firstName ?? '').trim();
    const mLast    = (match.lastName ?? '').trim();
    if (regFirst && regFirst !== mFirst) diffs.push('Vorname');
    if (regLast  && regLast  !== mLast)  diffs.push('Nachname');
    if (regEmail && regEmail !== mEmail) diffs.push('Email');
    if (regPhone && regPhone !== mPhone) diffs.push('Telefon');
    return diffs;
  };

  // -------------------------------------------------------------------------
  //  Einzel-Karte rendern (wiederverwendet für Einzel-Registrierungen und
  //  innerhalb von Sammelanmeldungs-Karten)
  // -------------------------------------------------------------------------
  // prediction: cumulative prediction of a Sammelanmeldung member (renderGroupCard); single cards predict alone.
  const renderMemberCard = (r, index, { standalone = true, isBusy = false, overrideWaitlist = false, prediction = null } = {}) => {
    const matches = goodMatches(r).map(m => ({
      ...m,
      diffs: m.score >= 90 ? computeContactDiffs(r, m) : []
    }));
    const topScore = matches[0]?.score;
    const sc = scoreClass(topScore);

    const admission = prediction ?? admissionOf(r);
    const waitlistReason = waitlistReasonOf(admission);
    // Sammelanmeldung with a capacity conflict: the dialog decides — the card only says "Warteliste".
    const eventFull     = waitlistReason === 'event' || overrideWaitlist;
    const addOnFull     = !eventFull && waitlistReason === 'addOn';
    const reductions    = !eventFull && !addOnFull ? admission.reductions : [];
    // Requested add-ons as name chips ("Leihausrüstung", "BBs ×2"); none → no chip.
    const requestedItems = addOnLineItems(requestedAddOnsOf(r));
    // Persönlicher Link: Gruppe des (per Token bekannten) Spielers am Spieltag.
    const matchedGroup = normalizeParticipantGroup(r.matchedParticipantGroup);
    // "Bei Annahme" (backend preview of a single approval) — inside a Sammelanmeldung the status or the booked
    // add-ons may differ (members in order, capacity conflict): then that status is shown instead of the previewed
    // mail, and the stale price gives way to a recalculation hint.
    const approvalOutcome = standalone ? null : groupApprovalOutcome(r, eventFull ? 'waitlist' : admission.status, admission);

    const cardContent = (
      <React.Fragment>
        <div className={`registrationCardAccent ${sc}`} />

        <div className={"registrationCardBody"}>
          <div className={"registrationCardHeader"}>
            <div className={"registrationCardName"}>
              <span className={"registrationCardFirstName"}>{r.firstName} </span>
              <span className={"registrationCardLastName"}>{r.lastName}</span>
            </div>
            <Tooltip text={`Eingegangen am ${formatDateTime(r.createdAt)}`}>
              <span className={"registrationCardTimestamp"}>
                <Icon className={"registrationCardTimestampIcon"} icon={'schedule'} />
                {formatDateTime(r.createdAt)}
              </span>
            </Tooltip>
          </div>

          <div className={"registrationCardOutcomeRow"}>
            {eventFull ? (
              <div className={"registrationCardOutcome waitlist"}>
                <Icon className={"registrationCardOutcomeIcon"} icon={'hourglass_empty'} />
                {overrideWaitlist && waitlistReason !== 'event'
                  ? 'Warteliste'
                  : <>Warteliste – Spieltag ausgebucht{selectedEventInfo?.maxPlayers > 0 && ` (${selectedEventInfo.confirmed}/${selectedEventInfo.maxPlayers})`}</>
                }
              </div>
            ) : addOnFull ? (
              <div className={"registrationCardOutcome addOnWaitlist"}>
                <Icon className={"registrationCardOutcomeIcon"} icon={'inventory_2'} />
                Zusatz-Warteliste – {waitlistAddOnText(admission.waitlistAddOns)}
              </div>
            ) : (
              <div className={"registrationCardOutcome confirmed"}>
                <Icon className={"registrationCardOutcomeIcon"} icon={'check_circle'} />
                Wird bestätigt
              </div>
            )}
            {isPersonalLinkSource(r) && (
              <Tooltip text={'Anmeldung über den persönlichen Link – der Spieler ist bereits bekannt'}>
                <span className={"registrationCardSourceBadge personalLink"}>
                  <Icon className={"registrationCardSourceBadgeIcon"} icon={'link'} />
                  Persönlicher Link
                </span>
              </Tooltip>
            )}
            {isPersonalLinkSource(r) && matchedGroup && (
              <ParticipantGroupChip group={matchedGroup} title={'Gruppe des Spielers am Spieltag'} />
            )}
          </div>

          {(r.email || r.phone || requestedItems.length > 0) && (
          <div className={"registrationCardMeta"}>
            {r.email && (
              <Tooltip text={'E-Mail'}>
                <a href={`mailto:${r.email}`} className={"registrationCardChip link"}>
                  <Icon className={"registrationCardChipIcon"} icon={'mail'} />
                  {r.email}
                </a>
              </Tooltip>
            )}
            {r.phone && (
              <Tooltip text={'Telefon'}>
                <a href={`tel:${r.phone}`} className={"registrationCardChip link"}>
                  <Icon className={"registrationCardChipIcon"} icon={'call'} />
                  {r.phone}
                </a>
              </Tooltip>
            )}
            {requestedItems.length > 0 && (
              <div className={"registrationCardAddOns"}>
                <Tooltip text={'Angefragte Zusätze'}>
                  <Icon className={"registrationCardChipIcon registrationCardAddOnsIcon"} icon={'inventory_2'} />
                </Tooltip>
                <AddOnChips items={requestedItems} ariaLabel={'Angefragte Zusätze'} />
              </div>
            )}
          </div>
          )}

          {/* Zusatz ohne Warteliste knapp: wird beim Annehmen gekürzt (kein Konflikt, keine Rückfrage). */}
          {reductions.length > 0 && (
            <div className={"registrationCardWarning capacity"}>
              <Icon className={"registrationCardWarningIcon"} icon={'inventory_2'} />
              <span>{reductions.map(reductionNote).join(' · ')}</span>
            </div>
          )}

          {matches.length > 0 ? (
            <MatchPillGroup matches={matches} approvalOutcome={approvalOutcome} />
          ) : (
            <div className={"registrationCardMatches"}>
              <Tooltip text={'Kein Spieler-Vorschlag gefunden'}>
                <span className={"registrationCardMatch none"}>
                  <Icon className={"registrationCardChipIcon"} icon={'person_off'} />
                  Kein Vorschlag
                </span>
              </Tooltip>
            </div>
          )}

          {/* Genau ein Vorschlag ("Übernehmen" wirkt auf ihn): Gruppe am Spieltag + was die Annahme
              auslöst. Bei mehreren Vorschlägen steht das je Kandidat in der Pill und im Auswahl-Popup. */}
          {matches.length === 1 && (
            <div className={"registrationCardApproval"}>
              <ParticipantGroupDot row={matches[0]} />
              <CandidateGroupMeta candidate={matches[0]} approval outcome={approvalOutcome} />
            </div>
          )}

          {r.message && (
            <div className={"registrationCardMessage"}>
              <Icon className={"registrationCardMessageIcon"} icon={'sticky_note_2'} />
              <span>"{r.message}"</span>
            </div>
          )}
        </div>

        {/* Aktions-Buttons nur für Einzel-Karten */}
        {standalone && (
          <div className={"registrationCardActions"}>
            {matches.length === 1 && (
              <ActionButton
                icon={addOnFull ? 'playlist_add' : 'check'}
                label={addOnFull ? 'Warteliste' : 'Übernehmen'}
                type={2}
                onClick={() => tryApprove(r, matches[0].playerId, false)}
                disabled={isBusy}
              />
            )}
            {matches.length > 1 && (
              <ActionButton
                icon={addOnFull ? 'playlist_add' : 'rule'}
                label="Wählen"
                type={2}
                onClick={() => askPickMatch(r)}
                disabled={isBusy}
              />
            )}
            <ActionButton
              icon="person_add"
              label="Neu anlegen"
              type={3}
              onClick={() => tryApprove(r, null, true)}
              disabled={isBusy}
            />
            <ActionButton
              icon="delete_sweep"
              label="Ablehnen"
              type={1}
              onClick={() => askReject(r)}
              disabled={isBusy}
            />
          </div>
        )}
      </React.Fragment>
    );

    if (standalone) {
      return (
        <motion.div
          key={r.id}
          className={`registrationCard ${isBusy ? 'busy' : ''}`}
          custom={index}
          variants={cardVariants}
          initial="initial"
          animate="visible"
        >
          {cardContent}
        </motion.div>
      );
    }

    return (
      <div key={r.id} className={"registrationGroupMember"}>
        {cardContent}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  //  Karte einer Sammelanmeldung rendern
  // -------------------------------------------------------------------------
  const renderGroupCard = (group, index) => {
    const isGroupBusy = busyGroup === group.groupToken;
    const availableSpots = availableSpotsOfEvent();
    const groupHasCapacityConflict = availableSpots < group.members.length;
    // Members in registration order, cumulative (what the backend does without a dialog choice): a member may land
    // on the add-on waitlist or get reduced add-ons because an earlier member took the last ones.
    const predictions = predictGroupDefault(group.members);
    const anyWaitlist = isEventFull()
      || group.members.some(r => predictions.get(r.id)?.status === 'addOnWaitlist');
    const addOnShortages = groupAddOnShortages(group.members, selectedEventInfo?.addOns ?? []);

    return (
      <motion.div
        key={group.groupToken}
        className={`registrationGroupCard ${isGroupBusy ? 'busy' : ''}`}
        custom={index}
        variants={cardVariants}
        initial="initial"
        animate="visible"
      >
        <div className={"registrationGroupHeader"}>
          <span className={"registrationGroupBadge"}>
            <Icon className={"registrationGroupBadgeIcon"} icon={'group'} />
            Sammelanmeldung · {group.members.length} Spieler
          </span>
        </div>

        <div className={"registrationGroupMembers"}>
          {group.members.map(r => renderMemberCard(r, 0, {
            standalone: false,
            overrideWaitlist: groupHasCapacityConflict,
            prediction: predictions.get(r.id) ?? null,
          }))}
        </div>

        {groupHasCapacityConflict && (
          <div className={"registrationCardWarning capacity registrationGroupCapacityWarning"}>
            <Icon className={"registrationCardWarningIcon"} icon={'hourglass_empty'} />
            <span>
              {availableSpots === 0
                ? `Spieltag ausgebucht – beim Annehmen wählst du: alle bestätigen, alle auf die Warteliste oder einzeln.`
                : `Nur ${availableSpots} ${availableSpots === 1 ? 'Platz' : 'Plätze'} frei für ${group.members.length} Spieler – beim Annehmen wählst du: alle bestätigen, alle auf die Warteliste oder einzeln.`
              }
            </span>
          </div>
        )}
        {!groupHasCapacityConflict && addOnShortages.length > 0 && (
          <div className={"registrationCardWarning capacity registrationGroupCapacityWarning"}>
            <Icon className={"registrationCardWarningIcon"} icon={'inventory_2'} />
            <span>
              {`${shortageSubject(addOnShortages)} nicht für alle – beim Annehmen wählst du je Spieler: bestätigen, Warteliste oder Zusatz-Warteliste.`}
            </span>
          </div>
        )}

        <div className={"registrationGroupActions"}>
          <ActionButton
            icon={(anyWaitlist || groupHasCapacityConflict) ? 'playlist_add' : 'group_add'}
            label="Annehmen"
            type={anyWaitlist ? undefined : 2}
            onClick={() => openGroupAccept(group)}
            disabled={isGroupBusy}
          />
          <ActionButton
            icon="delete_sweep"
            label="Ablehnen"
            type={1}
            onClick={() => askRejectGroup(group.groupToken)}
            disabled={isGroupBusy}
          />
        </div>
      </motion.div>
    );
  };

  return (
    <React.Fragment>
      <main className={"defaultExtendedMain"}>
        <OfflineBanner />

        {/* Filter-Leiste */}
        <div className={"registrationsFilterBar"}>
          <div className={"registrationsFilterDropdown"}>
            <CustomDropdown
              icon={selectedEventInfo?.linkSignup ? 'lock' : 'event'}
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              {eventOptions.length === 0 && (
                <option value="">– Keine Anmeldungen –</option>
              )}
              {eventOptions.map(opt => (
                <option key={opt.eventId} value={opt.eventId}>
                  {`${formatDate(opt.eventDate)}${opt.eventLabel ? ` · ${opt.eventLabel}` : ''}${opt.locationName ? ` · ${opt.locationName}` : ''}  (${opt.count} ${opt.count === 1 ? 'Anmeldung' : 'Anmeldungen'})`}
                </option>
              ))}
            </CustomDropdown>
          </div>
          {selectedEventInfo && (
            <div className={"registrationsFilterMeta"}>
              {selectedEventInfo.linkSignup && (
                <Tooltip text={'Anmeldung nur über den Einladungslink. Was die Annahme auslöst (Preis, Mail), steht beim jeweiligen Vorschlag.'} tapToShow>
                  <span className={"registrationsFilterChip negative"}>
                    <Icon className={"registrationsFilterChipIcon"} icon={'lock'} />
                    {selectedEventInfo.eventLabel || 'Einladungslink'}
                  </span>
                </Tooltip>
              )}
              <Tooltip text={'Eingetragene Spieler (bestätigt + Warteliste)'} tapToShow>
                <span className={"registrationsFilterChip"}>
                  <Icon className={"registrationsFilterChipIcon"} icon={'group'} />
                  {selectedEventInfo.confirmed}
                  {selectedEventInfo.maxPlayers > 0 ? ` / ${selectedEventInfo.maxPlayers}` : ''}
                </span>
              </Tooltip>
              {/* One counter per sign-up add-on of the process: "Leihausrüstung 5/8", tinted with the add-on color,
                  warning tone when exhausted (wins over the color). */}
              {addOnCounterItems(selectedEventInfo.addOns).map(item => {
                const isWarning = item.tone === 'warning';
                const colored = !isWarning && hasAddOnColor(item.colorKey);
                return (
                  <Tooltip key={item.key} text={item.tooltip} tapToShow>
                    <span
                      className={`registrationsFilterChip addOn${isWarning ? ' warning' : ''}${colored ? ' isAddOnColored' : ''}`}
                      style={colored ? addOnColorStyle(item.colorKey) : undefined}
                    >
                      <Icon className={"registrationsFilterChipIcon"} icon={'inventory_2'} />
                      <span className={"registrationsFilterChipLabel"}>{item.label}</span>
                    </span>
                  </Tooltip>
                );
              })}
              <Tooltip text={'Offene Anmeldungen'} tapToShow>
                <span className={"registrationsFilterChip accent"}>
                  <Icon className={"registrationsFilterChipIcon"} icon={'how_to_reg'} />
                  {selectedEventInfo.count}
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        {visibleItems.length === 0 && (
          <p className={"registrationsEmpty"}>
            {items.length === 0
              ? 'Aktuell keine offenen Anmeldungen.'
              : 'Für diesen Spieltag liegen keine offenen Anmeldungen vor.'}
          </p>
        )}

        <div className={"registrationsList"}>
          {groupedVisible.map((entry, index) => {
            if (entry.isGroup) {
              return renderGroupCard(entry, index);
            }
            const r = entry.reg;
            const isBusy = busyId === r.id;
            return renderMemberCard(r, index, { standalone: true, isBusy });
          })}
        </div>
      </main>

      {/* ---------- Einzel-Reject-Confirm ---------- */}
      <Popup name='confirmrejectregistration' confirmText='Ablehnen' headerText='Aktion bestätigen'
        type="confirmaction" onConfirm={handleConfirmReject} withCancel
      >
        <p>Möchtest du diese Anmeldung wirklich ablehnen? Es wird kein Spieler angelegt und keine Bestätigung verschickt.</p>
      </Popup>

      {/* ---------- Sammelanmeldung ablehnen ---------- */}
      <Popup name='confirmrejectgroup' confirmText='Sammelanmeldung ablehnen' headerText='Sammelanmeldung ablehnen'
        type="confirmaction" onConfirm={handleConfirmRejectGroup} withCancel
      >
        <p>Möchtest du die gesamte Sammelanmeldung ablehnen? Für <b>alle Personen</b> wird kein Spieler angelegt und keine Bestätigung verschickt.</p>
      </Popup>

      {/* ---------- Sammelanmeldung annehmen ---------- */}
      <Popup
        name='acceptgroup'
        headerText={`Sammelanmeldung annehmen (${groupAcceptState?.members?.length ?? 0} Spieler)`}
        type="info"
        withCancel
        withConfirm
        confirmText={groupAcceptSummary?.confirmText ?? 'Alle annehmen'}
        onConfirm={handleConfirmGroupAccept}
      >
        {groupAcceptState && (() => {
          const { hasCapacityConflict, availableSpots, members, mode, addOnShortages = [] } = groupAcceptState;
          const { spotsOverbooked, addOnsOverbooked, predictions } = groupAcceptSummary;
          const shortageIds = new Set(addOnShortages.map(s => s.addOnId));

          return (
            <div className={"registrationGroupAcceptList"}>
              {hasCapacityConflict && (
                <div className={"registrationGroupCapacityBanner"}>
                  <Icon className={"registrationGroupCapacityBannerIcon"} icon={'warning'} />
                  <span>
                    {availableSpots === 0
                      ? `Spieltag ausgebucht – die Sammelanmeldung hat ${members.length} Spieler.`
                      : `Nur noch ${availableSpots} ${availableSpots === 1 ? 'Platz' : 'Plätze'} frei – die Sammelanmeldung hat ${members.length} Spieler.`
                    }
                    {spotsOverbooked > 0 && <b>{` Spieltag wird um ${spotsOverbooked} überbucht.`}</b>}
                  </span>
                </div>
              )}
              {/* One banner per waitlist add-on the group wants more of than is left. */}
              {addOnShortages.map(s => {
                const over = (addOnsOverbooked ?? []).find(o => o.addOnId === s.addOnId);
                return (
                  <div key={s.addOnId} className={"registrationGroupCapacityBanner addOn"}>
                    <Icon className={"registrationGroupCapacityBannerIcon"} icon={'inventory_2'} />
                    <span>
                      {s.available === 0
                        ? `„${s.name}“ ist ausgebucht (${s.used}/${s.maxQuantity}) – ${personsWanting(s.persons, s.quantity)}.`
                        : `Nur noch ${s.available}× „${s.name}“ frei – ${personsWanting(s.persons, s.quantity)}.`
                      }
                      {over && <b>{` „${s.name}“ wird um ${over.by} überbucht.`}</b>}
                    </span>
                  </div>
                );
              })}
              {mode && (
                <SegmentedControl
                  ariaLabel={'Status für die ganze Sammelanmeldung'}
                  value={mode}
                  onChange={setGroupMode}
                  options={[
                    { value: 'allConfirmed', label: 'Alle bestätigen', icon: 'done_all',        tone: 'positive' },
                    { value: 'allWaitlist',  label: 'Alle Warteliste', icon: 'hourglass_empty', tone: 'warning' },
                    { value: 'individual',   label: 'Einzeln wählen',  icon: 'tune',            tone: 'accent' },
                  ]}
                />
              )}
              {members.map(m => {
                const candidates = goodMatches(m.reg);
                const decision = m.decision;
                const wish = requestedAddOnsOf(m.reg);
                const prediction = predictions?.get(m.reg.id);
                // The status this member really gets (dialog choice, or the rule in registration order).
                const memberStatus = prediction?.status ?? m.status;
                const memberReductions = memberStatus === 'confirmed' ? (prediction?.reductions ?? []) : [];
                // "Zusatz-Warteliste" only for members who asked for a short waitlist add-on — otherwise the mail
                // („… ausgebucht“) would name a wrong reason.
                const offerAddOnWaitlist = wish.some(w => shortageIds.has(w.addOnId));
                return (
                  <div key={m.reg.id} className={"registrationGroupAcceptMember"}>
                    <div className={"registrationGroupAcceptName"}>
                      <span>{m.reg.lastName}, {m.reg.firstName}</span>
                      {wish.length > 0 && (
                        <AddOnChips items={addOnLineItems(wish)} compact className={"registrationGroupAcceptAddOns"} ariaLabel={'Angefragte Zusätze'} />
                      )}
                    </div>
                    <div className={"registrationGroupAcceptRadios"}>
                      {candidates.map(c => (
                        <label key={c.playerId} className={`registrationGroupAcceptRadio ${scoreClass(c.score)}`}>
                          <input
                            type="radio"
                            name={`decision-${m.reg.id}`}
                            checked={!decision.createNew && decision.playerId === c.playerId}
                            onChange={() => setMemberDecision(m.reg.id, { playerId: c.playerId, createNew: false })}
                          />
                          <span className={"registrationGroupAcceptRadioName"}>
                            <ParticipantGroupDot row={c} />
                            {c.firstName} {c.lastName}
                            {c.namesSwapped && (
                              <span className={"swapCandidateHint"}>
                                <Icon className={"registrationCardChipIcon swap"} icon={'swap_horiz'} />
                                Namen vertauscht?
                              </span>
                            )}
                            <CandidateGroupMeta candidate={c} approval outcome={groupApprovalOutcome(m.reg, memberStatus, prediction)} />
                          </span>
                          <span className={`registrationCardScore ${scoreClass(c.score)}`}>{c.score}</span>
                          {c.alreadyInEvent && (
                            <span className={"registrationGroupAcceptAlready"}>
                              <Icon className={"registrationCardChipIcon warn"} icon={'warning'} />
                              bereits im Spieltag
                            </span>
                          )}
                          {c.previouslyInEvent && !c.alreadyInEvent && (
                            <span className={"registrationGroupAcceptRestore"}>
                              <Icon className={"registrationCardChipIcon restore"} icon={'history'} />
                              wird wiederhergestellt
                            </span>
                          )}
                        </label>
                      ))}
                      <label className={"registrationGroupAcceptRadio createNew"}>
                        <input
                          type="radio"
                          name={`decision-${m.reg.id}`}
                          checked={decision.createNew}
                          onChange={() => setMemberDecision(m.reg.id, { playerId: null, createNew: true })}
                        />
                        <span className={"registrationGroupAcceptRadioName"}>
                          Neuen Spieler anlegen
                        </span>
                        <Icon className={"registrationCardChipIcon"} icon={'person_add'} />
                      </label>
                    </div>
                    {(() => {
                      const candidate = !decision.createNew && decision.playerId
                        ? (m.reg.matchCandidates ?? []).find(c => c.playerId === decision.playerId)
                        : null;
                      const diffs = candidate && candidate.score >= 90
                        ? computeContactDiffs(m.reg, candidate)
                        : [];
                      if (!diffs.length) return null;
                      const fields = diffs.join(', ');
                      const verb = diffs.length === 1 ? 'weicht' : 'weichen';
                      const msg = m.keepOld
                        ? `${fields} ${verb} ab — alte Daten des Spielers bleiben erhalten.`
                        : `${fields} ${verb} ab — ${diffs.length === 1 ? 'wird' : 'werden'} beim Übernehmen aktualisiert.`;
                      return (
                        <div className={"registrationCardWarning contactChanged"}>
                          <Icon className={"registrationCardWarningIcon"} icon={'warning'} />
                          <span>{msg}</span>
                          <button
                            type="button"
                            className={"registrationCardWarningAction"}
                            onClick={() => toggleGroupMemberKeepOld(m.reg.id)}
                          >
                            {m.keepOld ? 'Neue übernehmen' : 'Aktuell behalten'}
                          </button>
                        </div>
                      );
                    })()}
                    {/* Add-ons without waitlist that are short for this member (cumulative, in registration
                        order): booked with less — a note, not a conflict. */}
                    {memberReductions.length > 0 && (
                      <div className={"registrationCardWarning capacity"}>
                        <Icon className={"registrationCardWarningIcon"} icon={'inventory_2'} />
                        <span>{memberReductions.map(reductionNote).join(' · ')}</span>
                      </div>
                    )}
                    {mode === 'individual' && (
                      <div className={"registrationGroupMemberStatus"}>
                        <SegmentedControl
                          size={'sm'}
                          ariaLabel={`Status für ${m.reg.firstName} ${m.reg.lastName}`}
                          value={m.status}
                          onChange={(status) => setGroupMemberStatus(m.reg.id, status)}
                          options={[
                            { value: 'confirmed', label: 'Bestätigt',  icon: 'check_circle',    tone: 'positive' },
                            { value: 'waitlist',  label: 'Warteliste', icon: 'hourglass_empty', tone: 'warning' },
                            ...(offerAddOnWaitlist
                              ? [{ value: 'addOnWaitlist', label: 'Zusatz-Warteliste', icon: 'inventory_2', tone: 'accent' }]
                              : []),
                          ]}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Popup>

      {/* ---------- Multi-Match-Auswahl ---------- */}
      <Popup name='pickmatchedplayer' headerText='Welcher Spieler?' type="info" withConfirm={false} withCancel>
        <p>Es gibt mehrere ähnliche Spieler. Wähle einen aus:</p>
        <div className={"registrationsPickList"}>
          {(matchPickItem.matchCandidates ?? []).map((m) => {
            const mc = scoreClass(m.score);
            return (
              <button
                type="button"
                key={m.playerId}
                className={`registrationsPickItem ${mc} ${m.alreadyInEvent ? 'alreadyInEvent' : ''} ${m.previouslyInEvent && !m.alreadyInEvent ? 'previouslyInEvent' : ''}`}
                onClick={() => handlePickMatch(matchPickItem, m)}
              >
                <span className={`registrationsPickScore ${mc}`}>{m.score}</span>
                <span className={"registrationsPickName"}>
                  <span className={"registrationsPickNameMain"}>
                    <ParticipantGroupDot row={m} />
                    {m.firstName} {m.lastName}
                  </span>
                  <CandidateGroupMeta candidate={m} approval />
                  {m.namesSwapped && (
                    <span className={"swapCandidateHint"}>
                      <Icon className={"registrationCardChipIcon swap"} icon={'swap_horiz'} />
                      Namen vertauscht?
                    </span>
                  )}
                  {m.alreadyInEvent && !m.email && (
                    <span className={"registrationsPickAlready"}>
                      <Icon className={"registrationCardChipIcon warn"} icon={'warning'} />
                      bereits im Spieltag
                    </span>
                  )}
                  {m.previouslyInEvent && !m.alreadyInEvent && !m.email && (
                    <span className={"registrationsPickRestore"}>
                      <Icon className={"registrationCardChipIcon restore"} icon={'history'} />
                      wird wiederhergestellt
                    </span>
                  )}
                </span>
                <span className={"registrationsPickContact"}>
                  {m.alreadyInEvent && m.email && (
                    <span className={"registrationsPickAlready"}>
                      <Icon className={"registrationCardChipIcon warn"} icon={'warning'} />
                      bereits im Spieltag
                    </span>
                  )}
                  {m.previouslyInEvent && !m.alreadyInEvent && m.email && (
                    <span className={"registrationsPickRestore"}>
                      <Icon className={"registrationCardChipIcon restore"} icon={'history'} />
                      wird wiederhergestellt
                    </span>
                  )}
                  <span>{m.email || '–'}{m.phone ? ` · ${m.phone}` : ''}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Popup>

      {/* ---------- Kontaktdaten-Abweichungs-Dialog (Einzelanmeldung) ---------- */}
      <Popup
        name='confirmcontactdiff'
        headerText='Kontaktdaten abweichend'
        type="info"
        withCancel
        withConfirm
        confirmText='Annehmen'
        onConfirm={handleConfirmContactDiff}
        dontCloseOnConfirm
        wide
      >
        {pendingContactDiff && (() => {
          const { diffs, reg: diffReg, candidate, keepOld } = pendingContactDiff;
          const fields = diffs.join(', ');
          const verb = diffs.length === 1 ? 'weicht' : 'weichen';
          return (
            <div className={"registrationContactDiffPopup"}>
              <div className={"registrationCardWarning contactChanged"}>
                <Icon className={"registrationCardWarningIcon"} icon={'warning'} />
                <span>
                  {fields} {verb} ab —{' '}
                  {keepOld
                    ? 'alte Daten des Spielers bleiben erhalten.'
                    : `${diffs.length === 1 ? 'wird' : 'werden'} beim Übernehmen aktualisiert.`}
                </span>
                <button
                  type="button"
                  className={"registrationCardWarningAction"}
                  onClick={toggleContactDiffKeepOld}
                >
                  {keepOld ? 'Neue übernehmen' : 'Aktuell behalten'}
                </button>
              </div>
              <table className={"registrationContactDiffTable"}>
                <tbody>
                  {diffs.includes('Vorname') && (
                    <tr>
                      <td className={"registrationContactDiffLabel"}>Vorname</td>
                      <td className={"registrationContactDiffOld"}>{candidate.firstName}</td>
                      <td className={"registrationContactDiffArrow"}>→</td>
                      <td className={"registrationContactDiffNew"}>{diffReg.firstName}</td>
                    </tr>
                  )}
                  {diffs.includes('Nachname') && (
                    <tr>
                      <td className={"registrationContactDiffLabel"}>Nachname</td>
                      <td className={"registrationContactDiffOld"}>{candidate.lastName}</td>
                      <td className={"registrationContactDiffArrow"}>→</td>
                      <td className={"registrationContactDiffNew"}>{diffReg.lastName}</td>
                    </tr>
                  )}
                  {diffs.includes('Email') && (
                    <tr>
                      <td className={"registrationContactDiffLabel"}>Email</td>
                      <td className={"registrationContactDiffOld"}>{candidate.email}</td>
                      <td className={"registrationContactDiffArrow"}>→</td>
                      <td className={"registrationContactDiffNew"}>{diffReg.email}</td>
                    </tr>
                  )}
                  {diffs.includes('Telefon') && (
                    <tr>
                      <td className={"registrationContactDiffLabel"}>Telefon</td>
                      <td className={"registrationContactDiffOld"}>{candidate.phone}</td>
                      <td className={"registrationContactDiffArrow"}>→</td>
                      <td className={"registrationContactDiffNew"}>{diffReg.phone}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Popup>

      {/* ---------- Korrektur-Popup für Email/Phone ---------- */}
      <Popup name='correctcontactdata' headerText='Kontaktdaten korrigieren'
        type="info" withCancel withConfirm confirmText='Übernehmen' onConfirm={handleConfirmCorrection} dontCloseOnConfirm
      >
        <p>Email und/oder Telefon entsprechen nicht dem erwarteten Format. Bitte vor Übernahme korrigieren:</p>
        <h3>E-Mail:</h3>
        <input
          className={"registrationsCorrectInput"}
          type="email"
          value={correctEmail}
          onChange={(e) => setCorrectEmail(e.target.value)}
          placeholder="name@domain.tld"
        />
        <h3>Telefon (Format +4915123456789):</h3>
        <input
          className={"registrationsCorrectInput"}
          type="tel"
          value={correctPhone}
          onChange={(e) => setCorrectPhone(e.target.value)}
          placeholder="+4915123456789"
        />
      </Popup>

      {/* ---------- Wartelisten-Bestätigung ---------- */}
      <Popup
        name='confirmwaitlist'
        headerText='Spieler auf Warteliste setzen?'
        type="warning"
        withCancel
        withConfirm
        confirmText='Zur Warteliste hinzufügen'
        onConfirm={handleConfirmWaitlist}
      >
        {pendingApproval?.waitlistReason === 'event' ? (
          <p>
            Der Spieltag ist ausgebucht
            {selectedEventInfo?.maxPlayers > 0
              ? ` (${selectedEventInfo.confirmed}/${selectedEventInfo.maxPlayers} Plätze belegt)`
              : ''}.
            <br />Der Spieler wird auf die <b>Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        ) : (
          <p>
            {(() => {
              const exhausted = pendingApproval?.waitlistAddOns ?? [];
              const names = joinNames(exhausted.map(a => `„${a.name}“`));
              const capacity = exhausted.length === 1 && exhausted[0].maxQuantity > 0
                ? ` (${exhausted[0].used}/${exhausted[0].maxQuantity})`
                : '';
              return `${names || 'Ein angefragter Zusatz'} ${exhausted.length > 1 ? 'sind' : 'ist'} ausgebucht${capacity}.`;
            })()}
            <br />Der Spieler wird auf die <b>Zusatz-Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel(ADD_ON_WAITLIST_SLOT)}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        )}
        <button type="button" className="registrationsForceConfirmBtn" onClick={handleForceConfirm}>
          <Icon className="registrationsForceConfirmIcon" icon="bolt" />
          Trotzdem bestätigen (Warteliste überspringen)
        </button>
      </Popup>

      {/* ---------- Error ---------- */}
      <Popup name='registrationerror' confirmText='Ok' headerText='Fehler' type="error">
        <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
      </Popup>
    </React.Fragment>
  );
}

export default Registrations;
